/*
 * lunna-reveal.js — one-shot viewport trigger for Lunna's micro-illustrations.
 *
 * Loaded only by the sections that carry an illustration, never globally, and
 * never a second animation framework: it sets three classes and gets out of
 * the way. Every keyframe lives in CSS.
 *
 * One IntersectionObserver serves every illustration on the page. A one-shot
 * element is unobserved the moment it plays, so nothing replays on the way back
 * up and nothing stays subscribed to scrolling. No scroll listener, no polling,
 * no per-element observer.
 *
 * `data-lunna-reveal="repeat"` opts an element out of that: it stays observed,
 * and the play class is taken off again when it leaves. This is for artwork
 * that loops rather than resolving to a finished frame — without it the loop
 * would keep running off screen, and a visitor scrolling back would find the
 * finished state instead of the artwork. One-shot stays the default; only the
 * elements that ask for it repeat.
 *
 * The contract with the CSS:
 *   .lunna-reveal--armed   JS is present and this element will animate. Only
 *                          inside this class may the CSS hide anything, and
 *                          only inside `prefers-reduced-motion: no-preference`.
 *   .lunna-reveal--in      the element has entered the viewport: play. On a
 *                          repeating element this class comes and goes with
 *                          the element, restarting the loop on each entry.
 *   .lunna-reveal--static  reduced motion, or no IntersectionObserver — the
 *                          finished artwork, rendered with no movement.
 *
 * Two attributes opt in, and they are not interchangeable:
 *
 *   data-lunna-reveal   the original — a micro-illustration whose keyframes
 *                       live in its own section stylesheet. Small elements,
 *                       played at 25% visibility. `="repeat"` loops.
 *
 *   data-lunna-motion   the shared vocabulary in lunna-motion.css: `rise`,
 *                       `cascade` or `curtain`. Any size, played as the
 *                       leading edge crosses in. `data-lunna-repeat` loops,
 *                       and `data-lunna-stagger` overrides the 65ms step on a
 *                       cascade container.
 *
 * Both take the same three classes; only the trigger geometry and the
 * stylesheet that reads them differ.
 *
 * With this script blocked the element keeps its default styling, which is the
 * finished illustration. Motion is the enhancement; the picture is not.
 */
(function () {
  'use strict';

  if (window.LunnaReveal) return;

  var SELECTOR = '[data-lunna-reveal], [data-lunna-motion]';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var supported = 'IntersectionObserver' in window;
  var pending = [];

  /* Two observers, and the difference is the whole reason there are two.
   *
   * A micro-illustration wants a quarter of itself on screen: it is small, and
   * waiting for that is what puts the drawing near the middle of the viewport
   * before it plays.
   *
   * A section cannot use that rule. A 3,300px section in a 900px viewport never
   * reaches a quarter of itself — it would stay armed, and therefore hidden,
   * forever. For those the trigger is the leading edge crossing in, which holds
   * at any height. This is why `data-lunna-motion` is a separate attribute
   * rather than another value on the old one.
   */
  var OBSERVERS = {
    reveal: { options: { threshold: 0.25, rootMargin: '0px 0px -8% 0px' }, instance: null },
    motion: { options: { threshold: 0, rootMargin: '0px 0px -22% 0px' }, instance: null },
  };

  function kindOf(element) {
    return element.hasAttribute('data-lunna-motion') ? 'motion' : 'reveal';
  }

  function repeats(element) {
    return element.dataset.lunnaReveal === 'repeat' || element.hasAttribute('data-lunna-repeat');
  }

  function play(element) {
    element.dataset.lunnaRevealState = 'played';
    element.classList.add('lunna-reveal--in');
  }

  /* Back to the armed state: the animations are removed with the class, so the
     loop stops off screen and starts from its first frame on the way back. */
  function rewind(element) {
    element.dataset.lunnaRevealState = 'armed';
    element.classList.remove('lunna-reveal--in');
  }

  function freeze(element) {
    element.dataset.lunnaRevealState = 'static';
    element.classList.remove('lunna-reveal--armed');
    element.classList.add('lunna-reveal--static');
  }

  function getObserver(kind) {
    var slot = OBSERVERS[kind];
    if (slot.instance) return slot.instance;

    slot.instance = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) {
          if (repeats(entry.target)) rewind(entry.target);
          return;
        }

        // Once only, unless the element asked to repeat. Unobserving here is
        // what stops the replay on scroll-up for everything else.
        if (!repeats(entry.target)) slot.instance.unobserve(entry.target);
        play(entry.target);
      });
    }, slot.options);

    return slot.instance;
  }

  /* Cascade writes each child's delay here rather than in the stylesheet. How
     many items a grid holds is the section's business, and an nth-child ladder
     only reaches as far as whoever wrote it remembered to go — the eleventh
     card would silently arrive with the tenth. */
  function stageCascade(element) {
    if (element.dataset.lunnaMotion !== 'cascade') return;

    var step = parseInt(element.dataset.lunnaStagger, 10);
    if (!(step > 0)) step = 65;

    var children = element.children;
    for (var i = 0; i < children.length; i++) {
      children[i].style.setProperty('--lunna-delay', i * step + 'ms');
    }
  }

  function register(element) {
    if (!element || element.dataset.lunnaRevealState) return;

    if (reduceMotion.matches || !supported) {
      freeze(element);
      return;
    }

    stageCascade(element);
    element.dataset.lunnaRevealState = 'armed';
    element.classList.add('lunna-reveal--armed');
    pending.push(element);
    getObserver(kindOf(element)).observe(element);
    bindFailsafe();
  }

  /* The late trigger has a failure mode and it is the worst one available: the
     last block on a page can run out of scroll before its top ever reaches the
     line, and an element that never plays is an element that stays hidden. So
     when the document bottom is reached — or when the page cannot scroll at
     all — everything still waiting plays where it stands. A visit never ends
     with content the visitor was not allowed to see. */
  var failsafeBound = false;

  function releaseStranded() {
    var doc = document.documentElement;
    var atBottom = window.innerHeight + window.scrollY >= doc.scrollHeight - 2;

    pending.slice().forEach(function (element) {
      if (element.dataset.lunnaRevealState !== 'armed') return;

      /* Two ways to be stranded, and the second only showed up once the whole
         store was converted. The page bottom is the obvious one. The other is
         an element sitting entirely inside the viewport that still never
         crossed the trigger line — a short section between two others, a
         collapsed block, anything the observer's shrunken root skipped past.
         Fully visible and still hidden is the worst state available, so it is
         checked on the same pass. */
      var box = element.getBoundingClientRect();
      var wholly = box.height > 0 && box.top >= 0 && box.bottom <= window.innerHeight;
      if (!atBottom && !wholly) return;
      var slot = OBSERVERS[kindOf(element)];
      if (slot.instance) slot.instance.unobserve(element);
      play(element);
    });
  }

  function bindFailsafe() {
    if (failsafeBound) return;
    failsafeBound = true;
    window.addEventListener('scroll', releaseStranded, { passive: true });
    window.addEventListener('resize', releaseStranded, { passive: true });
    // A short page may already be at its bottom on arrival.
    window.requestAnimationFrame(releaseStranded);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches(SELECTOR)) register(scope);
    scope.querySelectorAll(SELECTOR).forEach(register);
  }

  /* If the preference is switched on mid-visit, anything still armed would sit
     at its hidden start state forever. Settle those on the finished artwork. */
  function handlePreferenceChange() {
    if (!reduceMotion.matches) return;

    pending.splice(0).forEach(function (element) {
      if (element.dataset.lunnaRevealState !== 'armed') return;
      var slot = OBSERVERS[kindOf(element)];
      if (slot.instance) slot.instance.unobserve(element);
      freeze(element);
    });
  }

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', handlePreferenceChange);
  } else if (typeof reduceMotion.addListener === 'function') {
    reduceMotion.addListener(handlePreferenceChange);
  }

  window.LunnaReveal = { scan: scan, register: register };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan();
    });
  } else {
    scan();
  }

  // The theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });
})();
